/**
 * Reconcile stock with cost layers: preview first, apply only on purpose (batch 8 T11, #3032).
 *
 * The preview is a dry run that writes nothing. Applying is an explicit action on explicitly selected
 * keys with a confirmation that states exactly what happens and what does not: unbacked on-hand units
 * get an unknown-cost layer; no stock count, no cost and no historical movement is changed, and
 * over-backed or negative stock is shown but never repaired.
 *
 * The apply is ONE immutable intent (`useReconcileFlow`): the selection and its key are written down before
 * it is sent, survive leaving the route and a reload of the same session, and after an uncertain result
 * nothing else can be applied. The only ways on are to send the identical selection under the identical
 * key, or to abandon it knowingly. Everything that arrives late (a preview, an apply) is checked against
 * the session, filter and request it started under before it may change anything, and the whole panel
 * starts over when the session, tenant, user or permission changes.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { describeApiError } from '@/lib/describeApiError';
import type { WarehouseItem } from '@/hooks/useInventory';
import { useReconcileCosting, type ReconcileKey, type ReconcileOutcome } from '@/hooks/useInventoryCosting';
import { COSTING_CONFIRM_PERMISSION, fenceHolds, ownerKey, takeFence, useCostingAccess, type CostingScope } from './costingSession';
import { useReconcileFlow } from './useReconcileFlow';

const PAGE_SIZES = [25, 50, 100] as const;
const keyOf = (k: { partId: string; warehouseId: string }): string => `${k.partId}|${k.warehouseId}`;

export interface ReconcilePanelProps {
  readonly warehouses: readonly WarehouseItem[];
  /** May apply (`inventory.valuation.confirm`). Without it the preview is not offered either (the route needs it). */
  readonly canApply: boolean;
}

export function ReconcilePanel({ warehouses, canApply }: ReconcilePanelProps) {
  const access = useCostingAccess();
  // Nothing of the panel exists without the right to apply, and a new owner gets a new panel.
  if (!canApply || !access.canConfirm || access.scope === null) return null;
  return <ReconcileBody key={ownerKey(access)} warehouses={warehouses} scope={access.scope} />;
}

function ReconcileBody({ warehouses, scope }: { readonly warehouses: readonly WarehouseItem[]; readonly scope: CostingScope }) {
  const { t } = useTranslation('inventoryCosting');
  const { t: tc } = useTranslation('common');
  const previewer = useReconcileCosting();
  const flow = useReconcileFlow();
  const unresolved = flow.pending !== null;

  const [warehouseId, setWarehouseId] = useState('');
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState(0);
  const [preview, setPreview] = useState<ReconcileOutcome | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [selected, setSelected] = useState<Record<string, ReconcileKey>>({});
  const [error, setError] = useState<unknown>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [abandonOpen, setAbandonOpen] = useState(false);
  /** Which preview request is the latest: an answer to any other one is not shown. */
  const previewSeq = useRef(0);

  const send = previewer.mutateAsync;
  const runPreview = async (nextPage: number, size = pageSize, warehouse = warehouseId) => {
    // A listing while an apply is unresolved would rewrite the very selection that was sent.
    if (flow.pending !== null) return;
    const fence = takeFence(scope, COSTING_CONFIRM_PERMISSION);
    if (fence === null) return;
    const seq = ++previewSeq.current;
    const stale = () => seq !== previewSeq.current || !fenceHolds(fence);
    setError(null);
    flow.dismissOutcome();
    setPreviewing(true);
    try {
      const outcome = await send({
        request: { apply: false, ...(warehouse ? { warehouseId: warehouse } : {}), limit: size, offset: nextPage * size },
        scope,
      });
      if (stale()) return;
      setPreview(outcome);
      setPage(nextPage);
    } catch (e) {
      if (!stale()) setError(e);
    } finally {
      if (seq === previewSeq.current) setPreviewing(false);
    }
  };

  const changeWarehouse = (next: string) => {
    // What the old filter was answering is not the new filter's: drop it, and the selection made from it.
    previewSeq.current += 1;
    setPreviewing(false);
    setWarehouseId(next);
    setPreview(null);
    setSelected({});
    setPage(0);
  };

  // An accepted apply: what it created is announced from the flow, what was listed or selected is stale.
  const accepted = flow.outcome;
  useEffect(() => {
    if (accepted === null) return;
    previewSeq.current += 1;
    setPreviewing(false);
    setSelected({});
    setPreview(null);
  }, [accepted]);

  const selectedRows = Object.values(selected);
  const selectedUnits = selectedRows.reduce((sum, row) => sum + row.unbackedQuantity, 0);
  const busy = previewing || flow.phase === 'sending';
  const kindChip = (kind: ReconcileKey['kind']) => {
    const color = kind === 'unbacked' ? 'warning' : kind === 'negative_on_hand' ? 'error' : 'info';
    return <Chip size="small" color={color} variant="outlined" label={t(`reconcile.kind.${kind}`)} />;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-labelledby="reconcile-title">
      <Typography id="reconcile-title" variant="h6" gutterBottom>
        {t('reconcile.title')}
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('reconcile.explain')}
      </Alert>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField
          select
          size="small"
          label={t('filters.warehouse')}
          value={warehouseId}
          onChange={(event) => changeWarehouse(event.target.value)}
          disabled={unresolved}
          sx={{ minWidth: 220 }}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
        >
          <MenuItem value="">{t('filters.allWarehouses')}</MenuItem>
          {warehouses.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.name}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={() => void runPreview(0)} disabled={busy || unresolved}>
          {t('reconcile.preview')}
        </Button>
      </Box>

      {error !== null && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert" onClose={() => setError(null)}>
          {describeApiError(error, tc, { codePrefix: 'inventoryCosting:errors', fallback: t('errors.generic') })}
        </Alert>
      )}
      {flow.refusal !== null && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert" onClose={flow.dismissRefusal}>
          {describeApiError(flow.refusal, tc, { codePrefix: 'inventoryCosting:errors', fallback: t('errors.generic') })}
        </Alert>
      )}
      {flow.discardedStored && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={flow.dismissDiscardedStored}>
          {t('pending.discardedStored')}
        </Alert>
      )}
      {flow.storageUnreadable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('pending.storageUnreadable')}
        </Alert>
      )}
      {flow.pending && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          role="alert"
          action={
            <>
              <Button color="inherit" size="small" onClick={() => void flow.retry()} disabled={flow.phase === 'sending'}>
                {t('pending.retry')}
              </Button>
              <Button color="inherit" size="small" onClick={() => setAbandonOpen(true)} disabled={flow.phase === 'sending'}>
                {t('reconcile.abandon')}
              </Button>
            </>
          }
        >
          {flow.phase === 'sending'
            ? t('reconcile.sending', { count: flow.pending.keys.length })
            : flow.restored
              ? t('reconcile.restored', { count: flow.pending.keys.length })
              : t('reconcile.unknownResult', { count: flow.pending.keys.length })}
          {flow.persistence === 'unavailable' && <Box component="span"> {t('reconcile.notStored')}</Box>}
        </Alert>
      )}
      {accepted && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={flow.dismissOutcome}>
          {accepted.createdLayers.length > 0
            ? t('reconcile.applied', { layers: accepted.createdLayers.length, units: accepted.createdLayers.reduce((sum, l) => sum + l.quantity, 0) })
            : t('reconcile.appliedNothing')}{' '}
          {t('reconcile.appliedNext')}
        </Alert>
      )}

      {preview && (
        <>
          {preview.keys.length === 0 ? (
            <Alert severity="success">{t('reconcile.nothingToDo')}</Alert>
          ) : (
            <TableContainer>
              <Table size="small" aria-label={t('reconcile.tableLabel')}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t('table.part')}</TableCell>
                    <TableCell>{t('table.warehouse')}</TableCell>
                    <TableCell align="right">{t('reconcile.onHand')}</TableCell>
                    <TableCell align="right">{t('reconcile.layers')}</TableCell>
                    <TableCell align="right">{t('reconcile.drift')}</TableCell>
                    <TableCell>{t('reconcile.status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.keys.map((row) => {
                    const id = keyOf(row);
                    const selectable = row.kind === 'unbacked';
                    return (
                      <TableRow key={id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            disabled={!selectable || busy || unresolved}
                            checked={id in selected}
                            onChange={(event) =>
                              setSelected((prev) => {
                                const next = { ...prev };
                                if (event.target.checked) next[id] = row;
                                else delete next[id];
                                return next;
                              })
                            }
                            slotProps={{ input: { 'aria-label': t('reconcile.selectRow', { sku: row.sku }) } }}
                          />
                        </TableCell>
                        <TableCell>
                          {row.sku} · {row.name}
                        </TableCell>
                        <TableCell>{row.warehouseName}</TableCell>
                        <TableCell align="right">{row.onHand}</TableCell>
                        <TableCell align="right">{row.layerQuantity}</TableCell>
                        <TableCell align="right">{row.drift}</TableCell>
                        <TableCell>{kindChip(row.kind)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {preview.total !== null && (
            <TablePagination
              component="div"
              count={preview.total}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[...PAGE_SIZES]}
              onPageChange={(_event, next) => void runPreview(next)}
              onRowsPerPageChange={(event) => {
                const size = Number(event.target.value);
                setPageSize(size);
                void runPreview(0, size);
              }}
              labelRowsPerPage={t('table.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => t('table.displayedRows', { from, to, count })}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Button variant="contained" disabled={selectedRows.length === 0 || busy || unresolved} onClick={() => setConfirmOpen(true)}>
              {t('reconcile.apply', { count: selectedRows.length })}
            </Button>
            {busy && <CircularProgress size={20} aria-label={tc('loading')} />}
          </Box>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={t('reconcile.confirmTitle', { count: selectedRows.length })}
        message={t('reconcile.confirmMessage', { count: selectedRows.length, units: selectedUnits })}
        confirmText={t('reconcile.confirmAction')}
        loading={busy}
        onConfirm={() => {
          // One intent: the selection is written down with its key and sent from here, once.
          setConfirmOpen(false);
          void flow.start(selectedRows.map((row) => ({ partId: row.partId, warehouseId: row.warehouseId })));
        }}
        onCancel={() => setConfirmOpen(false)}
      />
      <ConfirmDialog
        open={abandonOpen}
        title={t('reconcile.abandonTitle')}
        message={t('reconcile.abandonMessage', { count: flow.pending?.keys.length ?? 0 })}
        confirmText={t('reconcile.abandonAction')}
        onConfirm={() => {
          setAbandonOpen(false);
          flow.discard();
        }}
        onCancel={() => setAbandonOpen(false)}
      />
    </Paper>
  );
}
